import { Checkbox } from '@fluentui/react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import RiskState, { RiskStates } from 'models/riskState';
import Tag from 'models/tag';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataRiskStatus } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { getRiskStateColorGradient } from 'utils/risk';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetRiskStatusConfig {
  tagIds: number[];

  showTags: boolean;

  tags: Tag[];

  filterGoalBelowCurrentNoOpenControls: boolean = false;

  constructor() {
    this.tagIds = [];
    this.showTags = false;
    this.tags = [];
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
        this.filterGoalBelowCurrentNoOpenControls = newRawConfig.filterGoalBelowCurrentNoOpenControls ?? false;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetRiskStatusConfig {
    const newConfig = new WidgetRiskStatusConfig();
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;
    newConfig.filterGoalBelowCurrentNoOpenControls = this.filterGoalBelowCurrentNoOpenControls;

    return newConfig;
  }
}

interface IWidgetRiskStatusProps extends IWidgetRendererProps {}

const WidgetRiskStatus: FunctionComponent<IWidgetRiskStatusProps> = (props: IWidgetRiskStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<RiskState[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [riskCount, setRiskCount] = useState<number>(0);
  const [config, setConfig] = useState<WidgetRiskStatusConfig | undefined>(undefined);
  const [colorGradient] = useState<string[]>(getRiskStateColorGradient());

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data = await apiGetWidgetDataRiskStatus(
          config.tags,
          config.filterGoalBelowCurrentNoOpenControls,
          accessToken,
          props.abortSignal,
        );

        setData(data.sort((a, b) => a.sortOrder - b.sortOrder));
        setRiskCount(data.reduce((sum, current) => sum + (current.riskCount ?? 0), 0));
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetRiskStatusConfig => {
    const newConfig = new WidgetRiskStatusConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

    return newConfig;
  };

  const getRiskStateColor = (idx: number): string => {
    return colorGradient[idx];
  };

  const getData = (): IPieChartDataPoint[] => {
    if (data.length === 0) return [];

    const config = loadConfig();
    const tagStr = `&tags=${config.tags.map((t) => t.tagId.toString()).join(',')}`;

    const graphData: IPieChartDataPoint[] = data.map((s, idx) => {
      const state: string = RiskStates[s.riskStateId];

      return {
        name: s.state,
        value: s.riskCount ?? 0,
        color: getRiskStateColor(idx),
        link: `/risks/overview?status=${state}${config.tags.length > 0 ? tagStr : ''}`,
      };
    });

    return graphData;
  };

  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (riskCount === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:RiskStatus.NoRisks')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <FluentUIPieChart
          data={getData()}
          height={config && config.tags.length > 0 && config.showTags ? 190 : 250}
          width={280}
          label={riskCount.toString()}
        ></FluentUIPieChart>
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetRiskStatus;

//
// Config
//

interface IWidgetConfigWidgetRiskStatusProps extends IWidgetConfigRendererProps {}

export const WidgetConfigRiskStatus: FunctionComponent<IWidgetConfigWidgetRiskStatusProps> = (
  props: IWidgetConfigWidgetRiskStatusProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [filterGoal, setFilterGoal] = useState<boolean>(false);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetRiskStatusConfig => {
    let newRawConfig = new WidgetRiskStatusConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
      newRawConfig.tags = appContext.globalDataCache.tags
        .getItemsForId(newRawConfig.tagIds)
        .filter((t) => t.tagId !== 0);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
      setFilterGoal(config.filterGoalBelowCurrentNoOpenControls);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    tags: Tag[],
    showTags: boolean,
    filterGoalBelowCurrentNoOpenControls: boolean,
  ): WidgetRiskStatusConfig => {
    const config = new WidgetRiskStatusConfig();
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;
    config.filterGoalBelowCurrentNoOpenControls = filterGoalBelowCurrentNoOpenControls;

    return config;
  };

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags, filterGoal);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags, filterGoal);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedTags, checked, filterGoal);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const updateFilterGoal = (checked: boolean) => {
    setFilterGoal(checked ?? false);
    const config = getConfig(selectedTags, showTags, checked);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPLabel>{t('widgets:RiskStatus.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
        ></KeyValueTagPicker>
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showTags}
          label={t('widgets:RiskStatus.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <Checkbox
          label={t('widgets:Risk.FilterScoreGoalBelowCurrentNoOpenControls')}
          checked={filterGoal}
          onChange={(ev, checked) => updateFilterGoal(checked ?? false)}
        />
      </IPStack.Item>
    </IPStack>
  );
};
